import { onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';

import { useUserStore } from '@/stores/user';
import { useSessionStore } from '@/stores/session';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useAvailableMapModes } from '@/components/top_level/navigationConsts/mapModes';
import { useStatsRoutes } from '@/components/top_level/navigationConsts/statsRoutes';
import { useSettingsRoutes } from '@/components/top_level/navigationConsts/settingsRoutes';
import { MAIN_MENU_REWRITES } from '@/components/top_level/navigationConsts/mainMenuRewrites';

const SEQUENCE_TIMEOUT_MS = 2000;
const DEBUG_KEYS = ['q', 'й'];

const isDigitKey = key => /^\d$/.test(key);

export function useKeyboardMenuNavigation() {
	const router = useRouter();
	const userStore = useUserStore();
	const sessionStore = useSessionStore();
	const baseStationsStore = useBaseStationsStore();

	const mapModes = useAvailableMapModes();
	const settingsRoutes = useSettingsRoutes();
	const statsRoutes = useStatsRoutes();

	let selectedMainMenuKey = null;
	let selectedStatsRouteIndex = null;
	let timeoutId = null;

	const clearSequence = () => {
		if (timeoutId != null) {
			clearTimeout(timeoutId);
		}
		timeoutId = null;
		selectedMainMenuKey = null;
		selectedStatsRouteIndex = null;
	};

	const scheduleSequenceTimeout = () => {
		if (timeoutId != null) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			clearSequence();
		}, SEQUENCE_TIMEOUT_MS);
	};

	const startSequence = () => {
		clearSequence();
		scheduleSequenceTimeout();
	};

	const parseDigit = key => {
		if (!/^\d$/.test(key)) {
			return null;
		}
		const index = Number.parseInt(key, 10);
		if (index === 0) {
			return 9;
		}
		if (index < 1) {
			return null;
		}
		return index - 1;
	};

	const navigateMainMenu = itemKey => {
		if (itemKey !== 'logout') {
			router.push({ name: itemKey });
		}
	};

	const navigateMainMenuRelative = step => {
		const menuItems = userStore.menuItems;
		if (menuItems.length === 0) {
			return;
		}
		const routeName = router.currentRoute.value.name;
		const normalizedRouteName = MAIN_MENU_REWRITES[routeName] || routeName;
		let activeIndex = menuItems.findIndex(item => item.key === normalizedRouteName);
		if (activeIndex < 0) {
			activeIndex = 0;
		}
		const targetIndex = (activeIndex + step + menuItems.length) % menuItems.length;
		navigateMainMenu(menuItems[targetIndex].key);
	};

	const applySubNavigation = submenuIndex => {
		if (selectedMainMenuKey === 'home-map') {
			if (submenuIndex >= mapModes.value.length) {
				return;
			}
			const mapMode = mapModes.value[submenuIndex];
			baseStationsStore.setMode(mapMode.key);
			router.push({ name: 'home-map' });
			return;
		}

		if (selectedMainMenuKey === 'stats') {
			if (submenuIndex >= statsRoutes.value.length) {
				return false;
			}
			const statsRoute = statsRoutes.value[submenuIndex];
			selectedStatsRouteIndex = submenuIndex;
			router.push({
				name: 'stats-type',
				params: { id: statsRoute.value },
			});
			return statsRoute.tabs != null && statsRoute.tabs.length > 0;
		}

		if (selectedMainMenuKey === 'settings') {
			if (submenuIndex >= settingsRoutes.value.length) {
				return false;
			}
			router.push({
				name: 'settings-type',
				params: { id: settingsRoutes.value[submenuIndex].value },
			});
			return false;
		}

		return false;
	};

	const applyStatsTabNavigation = tabIndex => {
		if (selectedMainMenuKey !== 'stats' || selectedStatsRouteIndex == null) {
			return false;
		}
		const statsRoute = statsRoutes.value[selectedStatsRouteIndex];
		if (statsRoute == null || statsRoute.tabs == null || tabIndex >= statsRoute.tabs.length) {
			return false;
		}
		router.push({
			name: 'stats-type',
			params: {
				id: statsRoute.value,
				subId: statsRoute.tabs[tabIndex].value,
			},
		});
		return true;
	};

	const handleDigitKey = key => {
		if (timeoutId == null) {
			clearSequence();
			return;
		}
		const itemIndex = parseDigit(key);
		if (itemIndex == null) {
			clearSequence();
			return;
		}

		scheduleSequenceTimeout();

		if (selectedMainMenuKey == null) {
			const menuItems = userStore.menuItems;
			if (itemIndex >= menuItems.length) {
				clearSequence();
				return;
			}
			selectedMainMenuKey = menuItems[itemIndex].key;
			navigateMainMenu(selectedMainMenuKey);

			if (!['home-map', 'stats', 'settings'].includes(selectedMainMenuKey)) {
				clearSequence();
			}
			return;
		}

		if (selectedStatsRouteIndex != null) {
			applyStatsTabNavigation(itemIndex);
			clearSequence();
			return;
		}

		const shouldAwaitThirdDigit = applySubNavigation(itemIndex);
		if (!shouldAwaitThirdDigit) {
			clearSequence();
		}
	};

	const keyListener = event => {
		const loweredKey = event.key.toLowerCase();

		if ((event.ctrlKey || event.metaKey) && DEBUG_KEYS.includes(loweredKey)) {
			sessionStore.toggleDebugMode();
			return;
		}

		if (!sessionStore.debug) {
			return;
		}

		if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowLeft') {
			clearSequence();
			navigateMainMenuRelative(-1);
			event.preventDefault();
			return;
		}
		if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowRight') {
			clearSequence();
			navigateMainMenuRelative(1);
			event.preventDefault();
			return;
		}

		if (isDigitKey(event.key)) {
			const hasModifier = event.ctrlKey || event.metaKey;
			if (hasModifier) {
				if (timeoutId == null) {
					startSequence();
				}
				handleDigitKey(event.key);
				event.preventDefault();
				return;
			}
			if (timeoutId != null) {
				handleDigitKey(event.key);
				event.preventDefault();
				return;
			}
		}
	};

	onMounted(() => document.addEventListener('keydown', keyListener));
	onBeforeUnmount(() => {
		document.removeEventListener('keydown', keyListener);
		clearSequence();
	});
}
